import type { ReactNode } from 'react'
import { StatusBadge } from '@lighthouse/design'
import {
  IconDatabase, IconHeartbeat, IconInbox, IconMessages, IconServer, IconStack2,
} from '@tabler/icons-react'
import type { StatusRole } from '@lighthouse/design'
import { NOT_MEASURED } from '../../utils/format'
import { linkRowProps } from './healthBoardFormat'

/**
 * One System Health row.
 *
 * Local rather than the design system's HealthRow, which takes a non-null
 * StatusRole by design (the union was deliberately not widened with an
 * 'unknown' member). A row nobody measured is a dash with its caption here — the
 * Sources page's rule — never a status. It reuses the lh-health-row classes, so
 * the two look alike where they overlap, and adds the detail line HealthRow has
 * no slot for.
 */
export function HealthLineRow({ rowKey, icon, name, detail, status, statusLabel, onOpen }: {
  rowKey: string
  icon?: ReactNode
  name: ReactNode
  detail?: string | null
  status: StatusRole | null
  statusLabel: string
  onOpen?: () => void
}) {
  const link = onOpen ? linkRowProps(onOpen, typeof name === 'string' ? `${name}: ${statusLabel}` : statusLabel) : {}
  return (
    <div className="lh-health-row" data-health-row={rowKey} data-status={status ?? 'unmeasured'} {...link}>
      {icon && <span className="lh-health-icon">{icon}</span>}
      <span style={{ flex: '1 1 auto', minWidth: 0 }}>
        <span className="lh-health-name" style={{ display: 'block' }}>{name}</span>
        {detail && (
          <span style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', overflowWrap: 'anywhere' }}>
            {detail}
          </span>
        )}
      </span>
      {status ? (
        <StatusBadge status={status} label={statusLabel} />
      ) : (
        <span style={{
          display: 'inline-flex', gap: 6, alignItems: 'center', flex: 'none', fontSize: 12,
          color: 'var(--color-text-disabled)', whiteSpace: 'nowrap',
        }}
        >
          <span aria-hidden="true">{NOT_MEASURED}</span>
          <span>{statusLabel}</span>
        </span>
      )}
    </div>
  )
}

/**
 * The icon for an /api/health group row, by its HEALTH_GROUPS key; an unmapped check gets the
 * heartbeat. A component rather than an exported map, so this file exports components only.
 */
export function HealthGroupIcon({ groupKey }: { groupKey: string }) {
  switch (groupKey) {
    case 'api': return <IconServer size={15} />
    case 'warehouse': return <IconDatabase size={15} />
    case 'cache': return <IconStack2 size={15} />
    case 'broker': return <IconMessages size={15} />
    case 'dead-letters': return <IconInbox size={15} />
    default: return <IconHeartbeat size={15} />
  }
}
